<template>
    <div :class="['plan', { [classNames]: true, [modalType]: true, 'active': isVisible, 'slide-out': !isVisible && modalType !== 'slide-out' }]"
        :style="modalStyles" @click.self="handleClickOutside">
        <div class="plan-header flex-md-row flex-column align-items-start align-items-md-center sm-gap-10" v-if="title">
            <div class="plan-breadcrumbs">
                <div class="plan-breadcrumbs-left">
                    <i class="iatse-icon-left-arrow close-plan-modal" @click="closeModal"></i>
                    <h3 v-if="!secondTitle">{{ title }}</h3>
                    <h3 v-else @click="closeModal">
                        <p class="plan-modal">{{ title }} </p>
                    </h3>
                    <h3 class="active pdl-10" v-if="secondTitle"> > {{ secondTitle }} </h3>
                </div>
                <div class="plan-switch">
                    <slot name="steps"> </slot>
                    <a v-for="(button, index) in headerButtons" :key="index" href="#"
                        :class="['btn plan-modal hide-mobile', { 'btn-primary': button?.type === 'primary', 'btn-secondary': button?.type === 'secondary' }]"
                        @click.prevent="handleButtonModal(button)">
                        {{ button.name }}
                    </a>
                    <div class="close-btn close-plan-modal" @click="closeModal"> <i class="iatse-icon-close"></i></div>
                </div>
            </div>
            <a v-for="(button, index) in headerButtons" :key="index" href="#"
                :class="['btn plan-modal hide-desktop w-sm-100', { 'btn-primary': button?.type === 'primary', 'btn-secondary': button?.type === 'secondary' }]"
                @click.prevent="handleButtonModal(button)">
                {{ button.name }}
            </a>
        </div>
        <component :is="getModalContent(modalType)" @close="closeModal" @close-above="closeAbove"
            @handle-submit="handleSubmit" @toggle-header-button="toggleHeaderButton" :fund="fund"
            :modalProps="modalProps" :index="index" />
    </div>
    <div v-if="modalType === 'success-modal'" class="modal-overlay success-modal-overlay"></div>
    <div v-if="modalType === 'smartsearch'" @click.self="closeOnOutsideClick" class="modal-overlay success-modal-overlay smartsearch"></div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, PropType, onMounted } from 'vue';
import { useModalStore } from '@/stores/modal';
import { useBeneficiaryStore } from '@/stores/beneficiary';
import { ModalProps, HeaderButton } from "@/interfaces/interfaces";
import { useParticipantStore } from '@/stores/participant';

export default defineComponent({
    name: 'ModalComponent',
    props: {
        isVisible: {
            type: Boolean,
            required: true
        },
        index: {
            type: Number,
            required: true
        },
        modalType: {
            type: String,
            default: ''
        },
        classNames: {
            type: String,
            default: ''
        },
        title: {
            type: String,
            default: ''
        },
        secondTitle: {
            type: String || null,
            default: null
        },
        fund: {
            type: String,
            default: null,
        },
        modalProps: {
            type: Object as PropType<ModalProps | null>,
            default: null,
        },
        headerButtons: {
            type: Array as PropType<HeaderButton[] | null>,
            default: null,
        },
        hasCloseConfirm: {
            type: Boolean,
            default: false,
        }
    },
    setup(props, { emit }) {
        const modalStore = useModalStore();
        const localHeaderButton = ref<HeaderButton[] | null>(props.headerButtons);

        const closeModal = () => {
            useParticipantStore().currentStep = 1;

            if(props.hasCloseConfirm) {
                useModalStore().openModal('success-modal', null, 'modal-success', null, {
                    title: 'Discard Changes?',
                    content: {
                        type: "text",
                        value: 'Are you sure you want to discard the changes? This action cannot be undone.',
                        icon: 'iatse-icon-user-verified'
                    },
                    confirmButtons: ["Cancel", "Confirm"],
                    onConfirm: () => emit('close', props.index)
                })
            } else {
                emit('close', props.index);
            }
        };

        const closeAbove = (index: number) => {
            emit('close-above', index);
        };

        const closeOnOutsideClick = (event: MouseEvent) => {
            const target = event.target as HTMLElement;

            if (!target.closest('.plan.smartsearch')) {
                modalStore.closeLastModal();
            }
        }

        const handleClickOutside = () => {
            if(props.hasCloseConfirm) {
                useModalStore().openModal('success-modal', null, 'modal-success', null, {
                    title: 'Discard Changes?',
                    content: {
                        type: "text",
                        value: 'Are you sure you want to discard the changes? This action cannot be undone.',
                        icon: 'iatse-icon-user-verified'
                    },
                    confirmButtons: ["Cancel", "Confirm"],
                    onConfirm: () =>  modalStore.closeAbove(props.index)
                })
            } else {
                modalStore.closeAbove(props.index);
            }
        }

        const handleButtonModal = (button: HeaderButton) => {
            if (button.name === 'Add Beneficiary') {
                useBeneficiaryStore().resetNewBene();
                useBeneficiaryStore().isAddAction = true;
                useBeneficiaryStore().isEditAction = false;
            }

            modalStore.openModal(button.action, button.secondTitle, button.classes, null, null, null, undefined, button.queryParams)
        }

        const toggleHeaderButton = (payload: HeaderButton[]) => {
            localHeaderButton.value = payload;
        }

        const modalStyles = computed(() => {
            const offset = 290 + Number(props.index * 20);

            return {
                "--modal-width": `calc(100% - ${offset}px)`,
            };
        });

        return {
            modalStyles,
            localHeaderButton,
            closeModal,
            closeAbove,
            handleClickOutside,
            closeOnOutsideClick,
            toggleHeaderButton,
            handleButtonModal,
            handleSubmit: modalStore.handleSubmit,
            getModalContent: modalStore.getModalContent,
        };
    }
});
</script>